import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Dev-only guard: on every route change it checks that no element overflows the
// viewport width, so horizontal-overflow layout regressions get caught early.
// It ignores elements inside intentional horizontal-scroll containers
// (overflow-x: auto/scroll/clip/hidden). Disabled entirely in production.
const SCROLLABLE_X = new Set(["auto", "scroll", "clip", "hidden"]);

function insideScrollableX(el) {
  let node = el.parentElement;
  while (node && node !== document.body) {
    const ox = getComputedStyle(node).overflowX;
    if (SCROLLABLE_X.has(ox)) return true;
    node = node.parentElement;
  }
  return false;
}

function describe(el) {
  const id = el.id ? `#${el.id}` : "";
  const cls = typeof el.className === "string" && el.className
    ? "." + el.className.trim().split(/\s+/).slice(0, 3).join(".")
    : "";
  const testid = el.getAttribute?.("data-testid");
  return `${el.tagName.toLowerCase()}${id}${cls}${testid ? `[data-testid=${testid}]` : ""}`;
}

function runCheck(pathname) {
  const vw = document.documentElement.clientWidth;
  const offenders = [];
  const nodes = document.body.querySelectorAll("*");
  for (const el of nodes) {
    const style = getComputedStyle(el);
    if (style.position === "fixed" || style.display === "none" || style.visibility === "hidden") continue;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) continue;
    // Element extends past the right edge (or starts before the left edge).
    if (rect.right > vw + 1 || rect.left < -1) {
      if (insideScrollableX(el)) continue;
      offenders.push({ el, right: Math.round(rect.right), left: Math.round(rect.left) });
    }
  }
  if (offenders.length) {
    // Keep only the outermost offenders (drop ones nested inside another offender).
    const outer = offenders.filter((o) => !offenders.some((p) => p.el !== o.el && p.el.contains(o.el)));
    console.warn(
      `%c[OverflowGuard] ${outer.length} element(s) overflow the viewport (${vw}px) on "${pathname}"`,
      "color:#E63946;font-weight:bold",
    );
    outer.slice(0, 10).forEach((o) => console.warn(`  → ${describe(o.el)}  (left=${o.left} right=${o.right})`, o.el));
  }
}

export default function OverflowGuard() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    // Check once layout settles and again after async content loads.
    const timers = [250, 900, 2000].map((d) => setTimeout(() => runCheck(pathname), d));
    return () => timers.forEach(clearTimeout);
  }, [pathname]);
  return null;
}
